import { AppointmentType } from "../client-site";
import { ICustomer } from './customer.interface';

export enum RecurringEventFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  BI_WEEKLY = 'bi-weekly',
  MONTHLY = 'monthly'
}

export interface IRecurringEvent {
  _id?: string;
  bid: string;
  type: AppointmentType;

  // Common fields
  title?: string;
  timeFrom: number;
  timeTo: number;
  providingClient: string;
  adminNote?: string;
  color?: string;

  // Recurrence pattern
  frequency: RecurringEventFrequency;
  startDate: Date; // ISO date string
  endDate?: Date | null; // ISO date string or null for never-ending

  customer?: string | ICustomer; // Customer ID or populated customer
  requiredService?: string[]; // Service IDs

  // Generated appointments
  generatedAppointments: string[];
  lastGeneratedDate: Date;
  // A real occurrence of the series (appointment-date convention). Regeneration steps from
  // here, so it follows the series when the owner moves it to another day. Missing on
  // series created before it existed — startDate is used instead.
  anchorDate?: Date;
  // Held while generateMoreEvents runs, so two runs can't generate the same dates.
  generationLockedUntil?: Date | null;

  // Status
  isActive: boolean;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

export interface ICreateRecurringEventDto {
  type: AppointmentType;
  title?: string;
  timeFrom: number;
  timeTo: number;
  providingClient: string;
  frequency: RecurringEventFrequency;
  startDate: string;
  endDate?: string | null;
  adminNote?: string;
  color?: string;

  // Customer appointment fields (required when eventType is CUSTOMER_APPOINTMENT)
  customer?: string;
  requiredService?: string[];
  /**
   * Total visit length in minutes, chosen by the owner instead of the services' own
   * duration. Applies to every appointment in the series.
   */
  durationOverride?: number;
}

export interface IUpdateRecurringEventDto {
  title?: string;
  timeFrom?: number;
  timeTo?: number;
  frequency?: RecurringEventFrequency;
  endDate?: string | null;
  referenceDate?: string; // The specific appointment date being edited
  newDate?: string; // The new date for that appointment

  // Can update customer appointment fields
  requiredService?: string[];
}


export interface IRecurringEventConflict {
  date: string;
  conflictingAppointmentId: string;
  conflictingAppointmentTitle: string;
  conflictingAppointmentType?: string; // To show what type of appointment conflicts
}

export interface ICheckRecurringEventConflictsResponse {
  hasConflicts: boolean;
  conflicts: IRecurringEventConflict[];
  totalEventsToCreate: number;
  generationLimit: Date;
}

export interface ICreateRecurringEventResponse {
  event: IRecurringEvent;
  /** How many appointments were actually generated for the series. */
  createdCount: number;
  /**
   * True when generation stopped early because the business hit its plan's monthly
   * appointment limit — the series exists, but covers fewer dates than requested.
   */
  limitReached: boolean;
}

export interface IGetRecurringAvailableSlotsResponse {
  futureAppointmentDates: string[];
  availableHours: number[];
}

export interface ICreateRecurringCustomerAppointmentDto {
  customer: string; // Customer ID
  requiredService: string[]; // Service IDs
  providingClient: string;
  frequency: RecurringEventFrequency;
  startDate: string;
  startHour: number;
  numberOfAppointments: number;
  treatmentSessionId?: string;
}
